import { AgenturModel } from 'app/models/agentur.model';
import { AgenturStore } from '../agentur.store';
import { computed, makeObservable, observable, runInAction, action } from 'mobx';
import { ISelectOpt } from 'app/components/common/Select';
import { formatBM } from 'app/utils';
import _ from 'lodash';
import { BaseUiStore } from './base.ui.store';
import { AgtKvmModel, AgtKvmStore } from '../agt.kvm.store';

export interface KvmMonthlyItem {
	agtId: number;
	bm: number;
	anzahlAnrufeGesamt: number;
	vuInbound: number;
}

export class KvmVM {
	agt: AgenturModel;
	@observable
	item: AgtKvmModel;
	@observable
	items: KvmMonthlyItem[] = [];

	constructor(agt: AgenturModel, item: AgtKvmModel) {
		makeObservable(this);
		this.agt = agt;
		this.item = item;
	}

	@computed
	get vuInbound() {
		return this.item ? this.item.vuInbound : 0;
	}

	@computed
	get anzahlAnrufeGesamt() {
		return this.item ? this.item.anzahlAnrufeGesamt : 0;
	}

	@computed
	get anzahlAnrufeWaehrendServicezeiten() {
		return this.item ? this.item.anzahlAnrufeWaehrendServicezeiten : 0;
	}

	@computed
	get ziel1Schaden() {
		return this.item ? this.item.ziel1Schaden : 0;
	}

	@computed
	get ziel2Vertragsanliegen() {
		return this.item ? this.item.ziel2Vertragsanliegen : 0;
	}

	@computed
	get ziel3SonstigeAnliegen() {
		return this.item ? this.item.ziel3SonstigeAnliegen : 0;
	}

	@computed
	get ziel3DannRetoure() {
		return this.item ? this.item.ziel3DannRetoure : 0;
	}

	@computed
	get retoure() {
		return this.item ? this.item.retoure : 0;
	}

	@computed
	get keineTaste() {
		return this.item ? this.item.keineTaste : 0;
	}

	@computed
	get schnellauflege() {
		return this.item ? this.item.schnellauflege : 0;
	}

	@computed
	get anzahlBm() {
		return this.item ? this.item.anzahlBm || 0 : 0;
	}

	// Legacy compatibility for fitness tracker
	@computed
	get anzahlAnrufeGesamtYtd() {
		// This is now the aggregated sum across the selected BM range
		return this.anzahlAnrufeGesamt;
	}

	// // Empty items array for backwards compatibility
	// // (aggregation is now handled server-side via analytics store)
	// get items() {
	// 	return [];
	// }
}

export class AgtKvmUiStore extends BaseUiStore<KvmVM> {
	name = 'AgtKvmUiStore';
	agtKvmStore: AgtKvmStore;
	agenturStore: AgenturStore;

	constructor(agenturStore: AgenturStore, agtKvmStore: AgtKvmStore) {
		super();
		makeObservable(this);
		this.agtKvmStore = agtKvmStore;
		this.agenturStore = agenturStore;
	}

	@observable
	bms: number[] = [];

	@observable
	minBmCurrent?: number;

	@observable
	maxBmCurrent?: number;

	@computed
	get bmsOpts(): ISelectOpt[] {
		return this.bms.map((bm) => {
			return {
				value: bm,
				label: formatBM(bm),
			};
		});
	}

	@computed
	get maxBm() {
		if (this.maxBmCurrent) return this.maxBmCurrent;
		return _.max(this.bms);
	}

	@computed
	get minBm() {
		if (this.minBmCurrent) return this.minBmCurrent;
		return _.min(this.bms);
	}

	@computed
	get minBmCurrentYear(): number {
		if (this.minBmCurrent) {
			const year = Math.floor(this.minBmCurrent / 100);
			if (year < new Date().getFullYear()) {
				// Return first month of current year (e.g., 202501 for January 2025)
				return new Date().getFullYear() * 100 + 1;
			}
			return this.minBmCurrent;
		}
		return 0;
	}

	@computed
	get maxBmCurrentYear(): number {
		if (this.maxBmCurrent) {
			const year = Math.floor(this.maxBmCurrent / 100);
			if (year > new Date().getFullYear()) {
				// Return last month of current year (e.g., 202512 for December 2025)
				return new Date().getFullYear() * 100 + 12;
			}
			return this.maxBmCurrent;
		}
		return 0;
	}

	@computed
	get currentBmItems() {
		// Items are already aggregated for the selected BM range
		return this.items;
	}

	async _baseLoad() {
		// Get available BMs from the analytics store
		const distinctBms = await this.agtKvmStore.getDistinctBMs();

		runInAction(() => {
			this.bms = distinctBms;
			// Set default BM range to all available months of the current year
			if (this.bms.length > 0) {
				const currentYear = new Date().getFullYear();
				const currentYearBms = this.bms.filter(bm => Math.floor(bm / 100) === currentYear);

				if (currentYearBms.length > 0) {
					this.minBmCurrent = currentYearBms[0];
					this.maxBmCurrent = currentYearBms[currentYearBms.length - 1];
				} else {
					// Fallback to most recent month if no current year data
					this.maxBmCurrent = this.bms[this.bms.length - 1];
					this.minBmCurrent = this.bms[this.bms.length - 1];
				}
			}
		});
	}

	@action
	async loadByBm(bm: number) {
		const cacheKey = `AgtKvmUiStore.loadByBm${bm}`;

		return this.withCache(
			cacheKey,
			async () => {
				console.time('AgtKvmUiStore.loadByBm');
				await this.load();

				// Load data for the specified BM from analytics store
				const agts = await this.agenturStore.findAll();
				const data = await this.agtKvmStore.loadByBm(bm);

				const res: KvmVM[] = [];
				runInAction(() => {
					// Group kvm data by agency
					agts.forEach((agt) => {
						const agtData = data.find((d) => d.agtId === agt.agtId);
						if (agtData) {
							const vm = new KvmVM(agt, agtData);
							res.push(vm);
						}
					});

					this.items = res;
				});
				console.timeEnd('AgtKvmUiStore.loadByBm');

				return res;
			},
			{
				cacheResult: true,
			}
		);
	}

	@action
	async aggregateAgtsByBm(minBm?: number, maxBm?: number) {
		// load() first, so the store defaults can serve as fallback. Without them an
		// undefined range queries ALL months and races against the filtered follow-up query.
		await this.load();
		minBm = minBm ?? this.minBm;
		maxBm = maxBm ?? this.maxBm;

		const cacheKey = `AgtKvmUiStore.aggregateAgtsByBm_${minBm || 'null'}_${maxBm || 'null'}`;

		return this.withCache(
			cacheKey,
			async () => {
				console.time('AgtKvmUiStore.aggregateAgtsByBm');

				// Get aggregated data from the analytics store
				const aggregatedData = await this.agtKvmStore.aggregateByAgt(minBm, maxBm);
				const agts = await this.agenturStore.findAll();

				// Group aggregated data by vtnrId
				const dataByVtnrId = new Map<number, AgtKvmModel>();
				aggregatedData.forEach((data) => {
					dataByVtnrId.set(data.vtnrId, data);
				});

				// Build results using direct Map lookup
				const res: KvmVM[] = agts
					.map((agt) => {
						const agtData = dataByVtnrId.get(agt.agtId);
						if (agtData) {
							return new KvmVM(agt, agtData);
						}
						return null;
					})
					.filter((vm): vm is KvmVM => vm !== null);

				runInAction(() => {
					this.items = res;
				});

				console.timeEnd('AgtKvmUiStore.aggregateAgtsByBm');
				return this.items;
			},
			{
				cacheResult: true,
				measureTime: true,
			}
		);
	}

	@action
	async loadLast3Months(): Promise<KvmMonthlyItem[]> {
		const last3Bms = this.bms.slice(-3);
		if (last3Bms.length === 0) return [];
		const minBm = last3Bms[0];
		const maxBm = last3Bms[last3Bms.length - 1];
		const cacheKey = `AgtKvmUiStore.loadLast3Months_${minBm}_${maxBm}`;

		return this.withCache(
			cacheKey,
			async () => {
				console.time('AgtKvmUiStore.loadLast3Months');

				// Load monthly data for last 3 months
				const monthlyData = await this.agtKvmStore.loadMonthlyByBmRange(minBm, maxBm);

				// Transform to KvmMonthlyItem format
				const result: KvmMonthlyItem[] = monthlyData.map((data) => ({
					agtId: data.vtnrId,
					bm: data.bm!,
					anzahlAnrufeGesamt: data.anzahlAnrufeGesamt,
					vuInbound: data.vuInbound || 0,
				}));

				// Group by agtId for assignment to KvmVM items
				const itemsByAgtId = new Map<number, KvmMonthlyItem[]>();
				result.forEach((item) => {
					if (!itemsByAgtId.has(item.agtId)) {
						itemsByAgtId.set(item.agtId, []);
					}
					itemsByAgtId.get(item.agtId)!.push(item);
				});

				// Assign monthly items to existing KvmVM instances
				runInAction(() => {
					this.items.forEach((vm) => {
						const monthlyItems = itemsByAgtId.get(vm.agt.agtId);
						if (monthlyItems) {
							vm.items = monthlyItems.sort((a, b) => a.bm - b.bm);
						}
					});
				});

				console.timeEnd('AgtKvmUiStore.loadLast3Months');
				return result;
			},
			{
				cacheResult: true,
				measureTime: true,
			}
		);
	}
}
